import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'semantic-ui/semantic-ui-imports';
import { ElectricReheater, ReheaterType, hasActive } from 'Types/Aggregates';
import { AppSettingsContext } from 'contexts/appSettings';
import { useData } from '../../contexts/data';
import OneLineInputs from '../FormInputs/OneLineInputs';
import FilterInput from '../FormInputs/FilterInput';
import FilterSelect from '../FormInputs/FilterSelect';

interface Props {
  updateField: (
    name: string,
    value: string | number | boolean | (string | number | boolean)[] | undefined
  ) => void;
  inputs: any;
  setInputs: (inputs: any) => void;
}

const AirflowInputs = ({ updateField, inputs, setInputs }: Props) => {
  const { t } = useTranslation();

  const { activeAggregate } = useData();
  const [reheaterOptions, setReheaterOptions] = React.useState<
    ElectricReheater[]
  >([]);
  const [reheaterTypes, setReheaterTypes] = React.useState<ReheaterType[]>([]);

  const { getInfoText } = React.useContext(AppSettingsContext);

  // console.log(inputs);
  // console.log(activeAggregate);

  React.useEffect(() => {
    if (activeAggregate) {
      const options =
        activeAggregate?.electric_reheaters?.sort((a, b) =>
          a.capacity < b.capacity ? -1 : 1
        ) || [];
      setReheaterOptions(options || []);

      const types: ReheaterType[] = [
        {
          key: 'none',
          value: 'NONE',
          text: t('common.none'),
        },
      ];

      if (options.length > 0) {
        types.push({
          key: 'ELECTRIC',
          value: 'ELECTRIC',
          text: t('productPage.winterTemp.reheatElectric'),
        });
      } else if (inputs['reheater_config.reheater_type'] === 'ELECTRIC') {
        setInputs({
          ...inputs,
          ...{
            'reheater_config.reheater_type': 'NONE',
          },
        });
      }

      if (activeAggregate?.water_reheater) {
        types.push({
          key: 'WATER',
          value: 'WATER',
          text: t('productPage.winterTemp.reheatWater'),
        });
      } else if (inputs['reheater_config.reheater_type'] === 'WATER') {
        setInputs({
          ...inputs,
          ...{
            'reheater_config.reheater_type': 'NONE',
          },
        });
      }

      setReheaterTypes(types);
    }
  }, [activeAggregate, inputs]);

  function selectElectricReheater(name: string, id: any) {
    setInputs({
      ...inputs,
      ...{
        'reheater_config.electric_reheater_id': id,
        'reheater_config.electric_reheater_cap': reheaterOptions.find(
          (o) => o.id === id
        )?.capacity,
      },
    });
  }

  return (
    <div className="product-page__inputs__align">
      <div className="product-page__inputs__group">
        <div className="product-filters__filter__title">
          {t('productFilters.airLevels.title')}
        </div>
        <OneLineInputs
          className="product-filters__filter__airflow-inputs"
          nameFirst="cooling.ambient.VIn"
          nameSecond="cooling.ambient.dp_external"
          label={t('productFilters.airLevels.enteringAir')}
          defaultFirstValue={inputs['cooling.ambient.VIn']}
          defaultSecondValue={inputs['cooling.ambient.dp_external']}
          labelUnitFirst="㎥/h"
          labelUnitSecond="Pa"
          disabled={true}
        />
        <OneLineInputs
          className="product-filters__filter__airflow-inputs"
          nameFirst="cooling.indoor.VIn"
          nameSecond="cooling.indoor.dp_external"
          label={t('productFilters.airLevels.exitingAir')}
          defaultFirstValue={inputs['cooling.indoor.VIn']}
          defaultSecondValue={inputs['cooling.indoor.dp_external']}
          labelUnitFirst="㎥/h"
          labelUnitSecond="Pa"
          disabled={true}
        />
        <div className="product-page__inputs__group__title">
          {t('productFilters.basicDataTemperature.title')}
        </div>
        <div className="product-page__inputs__group__sub-title">
          {t('productFilters.basicDataTemperature.summer')}
        </div>
        <OneLineInputs
          nameFirst="cooling.ambient.TIn"
          nameSecond="cooling.ambient.RHIn"
          label={t('productPage.sommerTemp.enteringAirTemp')}
          defaultFirstValue={inputs['cooling.ambient.TIn'] || ''}
          defaultSecondValue={inputs['cooling.ambient.RHIn'] || ''}
          onChange={(name, value) => updateField(name, value)}
        />
        <OneLineInputs
          nameFirst="cooling.indoor.TIn"
          nameSecond="cooling.indoor.RHIn"
          label={t('productPage.sommerTemp.exitingAirTemp')}
          defaultFirstValue={inputs['cooling.indoor.TIn'] || ''}
          defaultSecondValue={inputs['cooling.indoor.RHIn'] || ''}
          onChange={(name, value) => updateField(name, value)}
        />
        <FilterInput
          name="cooling.indoor.TOut"
          label={t('productPage.sommerTemp.expectedEnteringAirTemp')}
          unit="°C"
          defaultValue={inputs['cooling.indoor.TOut'] || 20}
          onChange={(name, val) => updateField(name, parseInt(val, 10))}
          inputProps={{ min: 5, max: 30, style: { maxWidth: '2.857rem' } }}
          formFieldProps={{ style: { width: 'calc(100% - 2.14rem)' } }}
          disableInputName={'cooling.indoor.calcTOut'}
          disabledValue={inputs ? inputs['cooling.indoor.calcTOut'] : false}
          onDisabledChange={(name, val) => updateField(name, val)}
        />
        <div className="product-page__inputs__group__sub-title">
          {t('productFilters.basicDataTemperature.winter')}
        </div>
        <OneLineInputs
          nameFirst="heating.ambient.TIn"
          nameSecond="heating.ambient.RHIn"
          label={t('productPage.winterTemp.enteringAirTemp')}
          defaultFirstValue={inputs['heating.ambient.TIn'] || ''}
          defaultSecondValue={inputs['heating.ambient.RHIn'] || ''}
          onChange={(name, value) => updateField(name, value)}
        />
        <OneLineInputs
          nameFirst="heating.indoor.TIn"
          nameSecond="heating.indoor.RHIn"
          label={t('productPage.winterTemp.exitingAirTemp')}
          defaultFirstValue={inputs['heating.indoor.TIn'] || ''}
          defaultSecondValue={inputs['heating.indoor.RHIn'] || ''}
          onChange={(name, value) => updateField(name, value)}
        />
        <FilterInput
          name="heating.indoor.TOut"
          label={t('productPage.winterTemp.expectedEnteringAirTemp')}
          unit="°C"
          defaultValue={inputs['heating.indoor.TOut'] || 20}
          onChange={(name, val) => updateField(name, parseInt(val, 10))}
          inputProps={{ min: 5, max: 30, style: { maxWidth: '2.857rem' } }}
          formFieldProps={{ style: { width: 'calc(100% - 2.14rem)' } }}
          disableInputName={'heating.indoor.calcTOut'}
          disabledValue={inputs ? inputs['heating.indoor.calcTOut'] : false}
          onDisabledChange={(name, val) => updateField(name, val)}
        />
      </div>
      <div className="product-page__inputs__group">
        <div className="product-page__inputs__group__title">
          {t('productPage.airFlow.title')}
        </div>

        <Form.Field>
          <label htmlFor="field9a">
            {t('productPage.winterTemp.heatEffectVex')}
          </label>
          <span>
            {activeAggregate?.system_data
              ? (
                  (activeAggregate.system_data.heating?.Qc_passive || 0) / 1000
                ).toFixed(2)
              : '-'}{' '}
            kW
          </span>
        </Form.Field>
        {activeAggregate && hasActive(activeAggregate) && (
          <>
            <Form.Field>
              <label htmlFor="field9">
                {t('productPage.winterTemp.heatEffectPump')}
              </label>
              <span>
                {activeAggregate?.system_data
                  ? (
                      ((activeAggregate.system_data.heating?.Qc || 0) -
                        (activeAggregate.system_data.heating?.Qc_passive ||
                          0)) /
                      1000
                    ).toFixed(2)
                  : '-'}{' '}
                kW
              </span>
            </Form.Field>
          </>
        )}
        <Form.Field>
          <label htmlFor="field9b">
            {t('productPage.winterTemp.heatEffectTotal')}
          </label>
          <span>
            {activeAggregate?.system_data
              ? ((activeAggregate.system_data.heating.Qc || 0) / 1000).toFixed(
                  2
                )
              : '-'}{' '}
            kW
          </span>
        </Form.Field>
        <Form.Field className="stacked">
          <label htmlFor="field10">
            {t('productPage.winterTemp.calculatedAirTemp')}
          </label>
          <span>
            {(
              (activeAggregate?.system_data.heating.supply.T_out || 273.15) -
              273.15
            ).toFixed(1)}{' '}
            °C{' '}
            {(
              (activeAggregate?.system_data.heating.supply.RH_out || 0) * 100
            ).toFixed(0)}
            %
          </span>
        </Form.Field>
        <FilterSelect
          name="reheater_config.reheater_type"
          label={t('productPage.winterTemp.calculateReheat')}
          value={inputs['reheater_config.reheater_type'] || 'none'}
          onChange={(name, value) => updateField(name, value)}
          disabled={inputs['heating.indoor.calcTOut'] !== true}
          options={reheaterTypes}
        />
        {inputs['heating.indoor.calcTOut'] === true && (
          <>
            {inputs['reheater_config.reheater_type'] === 'WATER' && (
              <FilterSelect
                name="reheater_config.coil_media"
                label={t('productPage.winterTemp.reheatMedia')}
                defaultValue={inputs['reheater_config.coil_media'] || 'WATER'}
                onChange={(name, value) => updateField(name, value)}
                options={[
                  {
                    key: 'WATER',
                    value: 'WATER',
                    text: t('productPage.winterTemp.water'),
                  },
                  {
                    key: 'ETYLENEGLYCOL',
                    value: 'ETYLENEGLYCOL',
                    text: t('productPage.winterTemp.reheatEtyleneGlycol'),
                  },
                  {
                    key: 'PROPYLENEGLYCOL',
                    value: 'PROPYLENEGLYCOL',
                    text: t('productPage.winterTemp.reheatPropyleneGlycol'),
                  },
                ]}
              />
            )}
            {inputs['reheater_config.reheater_type'] !== 'NONE' && (
              <>
                {['ELECTRIC', 'NONE'].indexOf(
                  inputs['reheater_config.reheater_type']
                ) === -1 &&
                  !!inputs['reheater_config.coil_media'] &&
                  inputs['reheater_config.coil_media'] !== 'WATER' && (
                    <FilterInput
                      name="reheater_config.coil_concentration"
                      label={t('productPage.winterTemp.concentration')}
                      unit="%"
                      disabledValue={
                        inputs ? inputs['heating.indoor.calcTOut'] : false
                      }
                      defaultValue={
                        inputs['reheater_config.coil_concentration'] || 20
                      }
                      onChange={(name, val) =>
                        updateField(name, parseInt(val, 10))
                      }
                      inputProps={{ min: 20, max: 60 }}
                    />
                  )}
                {inputs['reheater_config.reheater_type'] === 'ELECTRIC' && (
                  <>
                    {!!reheaterOptions?.length && (
                      <FilterSelect
                        name="reheater_config.electric_reheater_id"
                        label={t('productPage.winterTemp.calculateReheat')}
                        defaultValue={
                          inputs['reheater_config.electric_reheater_id'] || null
                        }
                        onChange={selectElectricReheater}
                        options={
                          reheaterOptions.map((er: ElectricReheater) => ({
                            key: er.id,
                            value: er.id,
                            text: `${(er.capacity / 1000).toFixed(1)} kW ${
                              er.power_supply === 'FOURHUNDRED'
                                ? '(400 V)'
                                : '(230 V)'
                            }`,
                          })) || []
                        }
                      />
                    )}
                  </>
                )}
                {inputs['reheater_config.reheater_type'] === 'ELECTRIC' && (
                  <Form.Field>
                    <label htmlFor="field19">
                      {t('productPage.winterTemp.effectNeed')}
                    </label>
                    <span>
                      {activeAggregate?.system_data
                        ? (
                            (activeAggregate.system_data.heating.reheater.Q ||
                              0) / 1000
                          ).toFixed(1)
                        : '-'}{' '}
                      kW
                    </span>
                  </Form.Field>
                )}
                {inputs['reheater_config.reheater_type'] !== 'ELECTRIC' && (
                  <>
                    <FilterInput
                      name="reheater_config.coil_supply"
                      label={t('productPage.winterTemp.coilSupply')}
                      unit="°C"
                      disabledValue={
                        inputs ? inputs['heating.indoor.calcTOut'] : false
                      }
                      defaultValue={inputs['reheater_config.coil_supply'] || 70}
                      onChange={(name, val) =>
                        updateField(name, parseInt(val, 10))
                      }
                    />
                    <FilterInput
                      name="reheater_config.coil_return"
                      label={t('productPage.winterTemp.coilReturn')}
                      unit="°C"
                      disabledValue={
                        inputs ? inputs['heating.indoor.calcTOut'] : false
                      }
                      defaultValue={inputs['reheater_config.coil_return'] || 40}
                      onChange={(name, val) =>
                        updateField(name, parseInt(val, 10))
                      }
                    />
                  </>
                )}
                <Form.Field className="stacked">
                  <label htmlFor="field21">
                    {t('productPage.winterTemp.calculatedAirTemp')}
                  </label>
                  <div>
                    <span>
                      {`${(
                        (activeAggregate?.system_data?.heating?.reheater
                          ?.T_out || 273.15) - 273.15
                      ).toFixed(1)} °C
                      ${(
                        (activeAggregate?.system_data?.heating?.reheater
                          ?.RH_out || 0) * 100
                      ).toFixed(0)}
                      %`}
                    </span>
                  </div>
                </Form.Field>
                {inputs['reheater_config.reheater_type'] === 'WATER' &&
                  !!activeAggregate?.system_data?.heating.reheater.valves
                    ?.length && (
                    <FilterSelect
                      name="kvs_size"
                      label={t('productPage.winterTemp.motorTube')}
                      defaultValue={
                        activeAggregate?.system_data?.heating?.reheater?.valves?.find(
                          (x) => x.key === inputs.kvs_size
                        )?.key ||
                        activeAggregate?.system_data?.heating?.reheater?.valves?.find(
                          (x) => x.standard
                        )?.key
                      }
                      onChange={(name, value) => updateField(name, value)}
                      options={
                        activeAggregate?.system_data?.heating?.reheater?.valves?.map(
                          (o) => ({
                            key: o.Kvs,
                            value: o.key,
                            text: `Kvs=${o.Kvs?.toFixed(1)}, N=${(
                              (o?.N || 0) * 100
                            )?.toFixed(0)}%, dp=${(
                              (o?.dp_tot || 0) / 1000
                            )?.toFixed(2)} kPa`,
                          })
                        ) || []
                      }
                      helpText={getInfoText('configform.kvs_size.help_text')}
                    />
                  )}
              </>
            )}
          </>
        )}
      </div>
      <div className="product-page__inputs__group">
        <div className="product-page__inputs__group__title">
          {t('productPage.airFlow.cooling')}
        </div>
        <Form.Field>
          <label htmlFor="field18">
            {t('productPage.sommerTemp.coolingEffectVex')}
          </label>
          <span>
            {activeAggregate?.system_data
              ? (activeAggregate.system_data.cooling.Qe_passive / 1000).toFixed(
                  2
                )
              : '-'}{' '}
            kW
          </span>
        </Form.Field>
        {activeAggregate && hasActive(activeAggregate) && (
          <>
            <Form.Field>
              <label htmlFor="field19">
                {t('productPage.sommerTemp.coolingEffectPump')}
              </label>
              <span>
                {activeAggregate?.system_data
                  ? (
                      (activeAggregate.system_data.cooling.Qe -
                        activeAggregate.system_data.cooling.Qe_passive) /
                      1000
                    ).toFixed(2)
                  : '-'}{' '}
                kW
              </span>
            </Form.Field>
          </>
        )}
        <Form.Field>
          <label htmlFor="field20">
            {t('productPage.sommerTemp.coolingEffectTotal')}
          </label>
          <span>
            {activeAggregate?.system_data
              ? ((activeAggregate.system_data.cooling.Qe || 0) / 1000).toFixed(
                  2
                )
              : '-'}{' '}
            kW
          </span>
        </Form.Field>
        <Form.Field className="stacked">
          <label htmlFor="field21">
            {t('productPage.sommerTemp.calculatedAirTemp')}
          </label>
          <div>
            <span>
              {(
                (activeAggregate?.system_data.cooling.supply.T_out || 273.15) -
                273.15
              ).toFixed(1)}{' '}
              °C{' '}
              {(
                (activeAggregate?.system_data?.cooling?.supply?.RH_out || 0) *
                100
              ).toFixed(0)}
              %
            </span>
          </div>
        </Form.Field>
      </div>
    </div>
  );
};
export default AirflowInputs;
